
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    useTable,
    useSortBy,
    useGlobalFilter,
    usePagination,
    useFilters,
} from "react-table";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";


import moment from "jalali-moment";
import { Calendar } from "@hassanmojab/react-modern-calendar-datepicker";
import "@hassanmojab/react-modern-calendar-datepicker/lib/DatePicker.css";
import { Trash2 } from "lucide-react";
import { FaFileExcel, FaFilePdf, FaSearch } from "react-icons/fa";

const CalendarInput = ({ label, value, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const ref = useRef();
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (ref.current && !ref.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const formatDate = (date) => {
        if (!date) return "";
        const m = moment
            .from(`${date.year}/${date.month}/${date.day}`, "fa", "YYYY/MM/DD")
            .locale("fa");
        return m.format("jYYYY/jMM/jDD");
    };

    return (
        <div ref={ref} className="relative w-48">
            <label className="text-sm mb-1 block">{label}</label>
            <div
                onClick={() => setIsOpen((prev) => !prev)}
                className="border rounded px-4 py-2 cursor-pointer bg-white"
            >
                {value ? formatDate(value) : "انتخاب تاریخ"}
            </div>
            {isOpen && (
                <div className="absolute z-50 bg-white p-2 border rounded shadow-md mt-1">
                    <div className="flex justify-end">
                        <button
                            onClick={() => setIsOpen(false)}
                            className="text-red-500 text-sm font-bold mb-2"
                        >
                            ×
                        </button>
                    </div>
                    <Calendar
                        value={value}
                        onChange={(val) => {
                            onChange(val);
                            setIsOpen(false);
                        }}
                        locale="fa"
                        calendarClassName="custom-calendar"
                        colorPrimary="#0fbcf9"
                        shouldHighlightWeekends
                    />
                </div>
            )}
        </div>
    );
};
const DiscountTable = ({ discounts: data, handleDeleteDiscount }) => {
    const [selectedMinDate, setSelectedMinDate] = useState(null);
    const [selectedMaxDate, setSelectedMaxDate] = useState(null);
    const [globalFilter, setGlobalFilter] = useState("");
    const [minPercent, setMinPercent] = useState("");
    const [maxPercent, setMaxPercent] = useState("");
    const [minDate, setMinDate] = useState("");
    const [maxDate, setMaxDate] = useState("");

    const sortedData = useMemo(() => {
        return [...data].sort(
            (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
        );
    }, [data]);
    const columns = useMemo(
        () => [
            {
                Header: "#",
                Cell: ({ row }) => row.index + 1,
                disableSortBy: true,
            },
            {
                Header: "کد تخفیف",
                accessor: "code",
            },
            {
                Header: "درصد",
                accessor: "percentage",
            },
            {
                Header: "تاریخ انقضا",
                accessor: "expireDate",
                Cell: ({ value }) => moment(value).format("jYYYY/jMM/jDD HH:mm"),
            }, {
                Header: " عدم استفاده ",
                accessor: "isUsed",
                Cell: ({ value }) => (
                    !value ? (
                        <span className="text-green-600 text-xl">✔</span>
                    ) : (
                        <span className="text-red-600 text-xl">✖</span>
                    )
                ),
            }, {
                Header: "عملیات",
                Cell: ({ row }) => (
                    <button
                        onClick={() => handleDeleteDiscount(row.original.code,row.original.id)}
                        className=" mx-auto flex items-center justify-center gap-1 px-1 py-1 text-red-600  rounded-lg hover:bg-red-600 hover:text-white transition-all duration-200 text-sm"
                    >
                        <Trash2 size={16} />
                        
                    </button>
                ),
            },
        ],
        [handleDeleteDiscount]
    );
    useEffect(() => {
        if (selectedMinDate) {
            const m = moment
                .from(
                    `${selectedMinDate.year}/${selectedMinDate.month}/${selectedMinDate.day}`,
                    "fa",
                    "YYYY/MM/DD"
                )
                .startOf("day");
            setMinDate(m.toISOString());
        } else {
            setMinDate("");
        }

        if (selectedMaxDate) {
            const m = moment
                .from(
                    `${selectedMaxDate.year}/${selectedMaxDate.month}/${selectedMaxDate.day}`,
                    "fa",
                    "YYYY/MM/DD"
                )
                .endOf("day");
            setMaxDate(m.toISOString());
        } else {
            setMaxDate("");
        }
    }, [selectedMinDate, selectedMaxDate]);

    const filteredData = useMemo(() => {
        if (!Array.isArray(sortedData)) return [];

        return sortedData.filter((item) => {
            const code = item.code || "";
            const percentage = item.percentage ?? null;
            const expireDate = item.expireDate ?? null;

            const matchesCode = code
                .toString()
                .toLowerCase()
                .includes((globalFilter || "").toLowerCase());

            const matchesPercent =
                (!minPercent || (percentage !== null && percentage >= +minPercent)) &&
                (!maxPercent || (percentage !== null && percentage <= +maxPercent));

            const itemDate = expireDate ? moment(expireDate) : null;
            const matchesDate =
                (!minDate || (itemDate && itemDate.isSameOrAfter(moment(minDate)))) &&
                (!maxDate || (itemDate && itemDate.isSameOrBefore(moment(maxDate))));

            return matchesCode && matchesPercent && matchesDate;
        });
    }, [sortedData, globalFilter, minPercent, maxPercent, minDate, maxDate]);

    const tableInstance = useTable(
        {
            columns,
            data: filteredData,
            initialState: { pageSize: 5 },
        },
        useGlobalFilter,
        useFilters,
        useSortBy,
        usePagination
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        page,
        prepareRow,
        nextPage,
        previousPage,
        canNextPage,
        canPreviousPage,
        setPageSize,
        state: { pageIndex, pageSize },
    } = tableInstance;
    const exportToExcel = () => {
        const mappedData = filteredData.map((item) => ({
            "کد تخفیف": item.code,
            "درصد تخفیف": Number(item.percentage).toLocaleString("en-US"), // 👈🏻 تبدیل مبلغ با کاما
            "تاریخ انقضا": moment(item.expireDate).format("jYYYY/jMM/jDD HH:mm"),
        }));
    
        const ws = XLSX.utils.json_to_sheet(mappedData, { origin: "A2" });
    
        // ساخت تیتر بالای جدول
        XLSX.utils.sheet_add_aoa(ws, [["لیست تراکنش‌ها"]], { origin: "A1" });
    
        // Merge تیتر روی سه ستون
        ws["!merges"] = [
            {
                s: { r: 0, c: 0 },
                e: { r: 0, c: 2 },
            },
        ];
    
        // تعیین عرض ستون‌ها
        ws["!cols"] = [
            { wch: 40 }, // کد تراکنش
            { wch: 20 }, // مبلغ (یکم بیشتر چون با کاما شده)
            { wch: 25 }, // تاریخ
        ];
    
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "transactionList");
    
        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const dataBlob = new Blob([excelBuffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        saveAs(dataBlob, "transactionList.xlsx");
    };
    const exportToPDF = () => {
        const doc = new jsPDF();

        // doc.addFileToVFS("Vazir.ttf", vazirFont);
        // doc.addFont("Vazir.ttf", "Vazir", "normal");
        doc.setFont("Vazir")
        const tableColumn = ["کد تخفیف", "درصد تخفیف", "تاریخ انقضا"];
        const tableRows = filteredData.map((item) => [
            item.code,
            item.percentage,
            moment(item.expireDate).format("jYYYY/jMM/jDD HH:mm"),
        ]);

        autoTable(doc, {
            head: [tableColumn],
            body: tableRows,
            styles: {
                font: "Vazir",
                fontStyle: "normal",
                halign: "center",
            },
            headStyles: {
                halign: "center",
            },
        });
        doc.save("transactionList.pdf");
    };
    // const exportToExcel = () => {
    //     const ws = XLSX.utils.json_to_sheet(filteredData);
    //     const wb = XLSX.utils.book_new();
    //     XLSX.utils.book_append_sheet(wb, ws, "Discounts");
    //     const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    //     const dataBlob = new Blob([excelBuffer], {
    //         type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    //     });
    //     saveAs(dataBlob, "discounts.xlsx");
    // }; 


    // const exportToPDF = () => {
    //     const doc = new jsPDF();
    //     const tableColumn = ["discount code", "percentage", "expireDate "];
    //     const tableRows = filteredData.map((item) => [
    //         item.code,
    //         item.percentage,
    //         moment(item.expireDate).format("jYYYY/jMM/jDD HH:mm"),
    //     ]);

    //     autoTable(doc, {
    //         head: [tableColumn],
    //         body: tableRows,
    //     });

    //     doc.save("discounts.pdf");
    // };

    const resetFilters = () => {
        setGlobalFilter("");
        setMinPercent("");
        setMaxPercent("");
        setSelectedMinDate(null);
        setSelectedMaxDate(null);
    };

    return (
        <div className="p-4  max-w-5xl mx-auto">
            <div className="space-y-4 mb-5 mt-10">

                <div className="flex flex-wrap gap-4 items-center">
                    <label htmlFor="search" className="text-gray-700 font-semibold text-lg">
                        جستجوی کد تخفیف
                    </label>
                    <div className="relative w-full md:w-1/2">
                        <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                        <input
                            id="search"
                            value={globalFilter}
                            onChange={(e) => setGlobalFilter(e.target.value)}
                            placeholder="جستجوی کد تخفیف..."
                            className="border rounded px-4 py-2 pl-10 w-full text-sm focus:border-[#aadec5] focus:outline-none focus:ring-2 focus:ring-[#aadec5]"
                        />
                    </div>
                </div>

                {/* سطر دوم: فیلترها و دکمه‌ها */}
                <div className="flex flex-wrap gap-8 items-end flex-grow">
                    <div className="flex flex-wrap sm:flex-nowrap gap-4">
                        {/* حداقل درصد */}
                        <div className="flex flex-col">
                            <label className="text-sm text-gray-900 mb-1"> حداقل تخفیف </label>
                            <input
                                type="number"
                                value={minPercent}
                                onChange={(e) => setMinPercent(e.target.value)}
                                className="border border-gray-100 rounded px-4 py-2 w-32 h-9"
                            />
                        </div>

                        {/* حداکثر درصد */}
                        <div className="flex flex-col">
                            <label className="text-sm text-gray-900 mb-1"> حداکثر تخفیف</label>
                            <input
                                type="number"
                                value={maxPercent}
                                onChange={(e) => setMaxPercent(e.target.value)}
                                className="border border-gray-100 rounded px-4 py-2 w-32 h-9"
                            />
                        </div>
                    </div>
                    <div className="flex flex-wrap sm:flex-nowrap gap-4">
                        {/* تاریخ‌ها */}
                        <CalendarInput
                            label="از تاریخ"
                            value={selectedMinDate}
                            onChange={setSelectedMinDate}
                        />
                        <CalendarInput
                            label="تا تاریخ"
                            value={selectedMaxDate}
                            onChange={setSelectedMaxDate}
                        />
                    </div>
                    {/* دکمه‌ها */}
                    <button
                        onClick={resetFilters}
                        className="bg-yellow-400 px-4 py-2 rounded text-white h-9 mr-auto"
                    >
                        حذف فیلترها
                    </button>

                </div>
                
            </div>



            <div className="overflow-x-auto">
                <table {...getTableProps()} className="min-w-full bg-white border border-gray-300  rounded-xl shadow-md text-sm sm:text-base">
                    <thead className="bg-gray-100">
                        {headerGroups.map((headerGroup) => (
                            <tr key={headerGroup.id} {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map((column) => (
                                    <th
                                        key={column.id}
                                        {...column.getHeaderProps(column.getSortByToggleProps())}
                                        className="text-center px-1  py-2 border-b border-gray-300 cursor-pointer whitespace-nowrap"
                                    >
                                        {column.render("Header")}
                                        <span>
                                            {column.isSorted ? (column.isSortedDesc ? " 🔽" : " 🔼") : ""}
                                        </span>
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                        {page.map((row) => {
                            prepareRow(row);
                            return (
                                <tr key={row.id || row.original.id} {...row.getRowProps()}>
                                    {row.cells.map((cell) => (
                                        <td key={cell.column.id} {...cell.getCellProps()} className="px-1 py-2 border-b text-center">
                                            {cell.render("Cell")}
                                        </td>
                                    ))}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            <div className="flex flex-wrap gap-2 items-end justify-end mt-5">
                    <button onClick={exportToExcel} className="bg-green-500 px-4 py-2 rounded text-white">
                        <FaFileExcel />
                    </button>
                    <button onClick={exportToPDF} className="bg-red-500 px-4 py-2 rounded text-white">
                        <FaFilePdf />
                    </button>

                </div>
            <div className="flex items-center justify-between mt-4">
                <div className="flex items-center gap-2">
                    <span>تعداد ردیف در صفحه:</span>
                    <select
                        value={pageSize}
                        onChange={(e) => setPageSize(Number(e.target.value))}
                        className="border rounded  py-2"
                    >
                        {[5, 10, 20].map((size) => (
                            <option key={size} value={size}>
                                {size}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="flex gap-2">
                    <button
                        onClick={previousPage}
                        disabled={!canPreviousPage}
                        className="px-4 py-2 border rounded disabled:opacity-50"
                    >
                        قبلی
                    </button>
                    <button
                        onClick={nextPage}
                        disabled={!canNextPage}
                        className="px-4 py-2 border rounded disabled:opacity-50"
                    >
                        بعدی
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DiscountTable;
